import { useLanguage } from '@/contexts/LanguageContext';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import SEO from '@/components/SEO';

const PrivacyPolicy = () => {
  const { lang } = useLanguage();

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEO
        path="/privacy"
        title={lang === 'cs' ? 'Ochrana osobních údajů | Tom Art Mask' : 'Privacy Policy | Tom Art Mask'}
        description={lang === 'cs' ? 'Zásady zpracování osobních údajů a GDPR informace pro Tom Art Mask.' : 'Privacy policy and GDPR information for Tom Art Mask.'}
      />
      <div className="max-w-3xl mx-auto px-4 py-12">
        <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors mb-8">
          <ArrowLeft className="w-4 h-4" />
          {lang === 'cs' ? 'Zpět na hlavní stránku' : 'Back to homepage'}
        </Link>

        {lang === 'cs' ? <CzechPrivacy /> : <EnglishPrivacy />}
      </div>
    </div>
  );
};

const CzechPrivacy = () => (
  <article className="prose prose-invert max-w-none prose-headings:text-foreground prose-p:text-muted-foreground prose-li:text-muted-foreground prose-strong:text-foreground">
    <h1>Ochrana osobních údajů</h1>
    <p className="text-sm text-muted-foreground">Poslední aktualizace: {new Date().toLocaleDateString('cs-CZ')}</p>

    <h2>1. Správce osobních údajů</h2>
    <p>
      Správcem osobních údajů je:<br />
      <strong>Tomáš Veselý</strong>, OSVČ<br />
      IČO: <strong>[DOPLNIT IČO]</strong><br />
      Sídlo: <strong>[DOPLNIT ADRESU]</strong><br />
      Email: <strong>info@tomartmask.cz</strong>
    </p>

    <h2>2. Jaké osobní údaje zpracováváme</h2>
    <p>Prostřednictvím kontaktního formuláře a konfigurátoru masek na tomto webu zpracováváme:</p>
    <ul>
      <li>Jméno a příjmení</li>
      <li>E-mailovou adresu</li>
      <li>Telefonní číslo (volitelné)</li>
      <li>Obsah zprávy / poptávky</li>
    </ul>
    <p>Prostřednictvím chatbotu můžeme zpracovávat:</p>
    <ul>
      <li>E-mailovou adresu (pokud ji poskytnete)</li>
      <li>Telefonní číslo (pokud jej poskytnete)</li>
      <li>Obsah konverzace</li>
    </ul>

    <h2>3. Účel zpracování</h2>
    <ul>
      <li><strong>Vyřízení poptávky</strong> – odpověď na vaši zprávu, příprava nabídky, komunikace ohledně zakázky</li>
      <li><strong>Oprávněný zájem</strong> – ochrana práv správce, zlepšování služeb</li>
    </ul>

    <h2>4. Právní základ zpracování</h2>
    <p>
      Zpracování probíhá na základě <strong>čl. 6 odst. 1 písm. b) GDPR</strong> (plnění smlouvy / přijetí opatření před uzavřením smlouvy) a <strong>čl. 6 odst. 1 písm. f) GDPR</strong> (oprávněný zájem správce).
    </p>

    <h2>5. Doba uchovávání</h2>
    <p>
      Osobní údaje z kontaktních formulářů uchováváme po dobu nezbytnou pro vyřízení poptávky, maximálně <strong>3 roky</strong> od posledního kontaktu, pokud není potřeba je uchovávat déle z důvodu plnění zákonných povinností.
    </p>

    <h2>6. Cookies a služby třetích stran</h2>
    <p>Tento web využívá:</p>
    <ul>
      <li><strong>YouTube embed</strong> – při přehrávání videí mohou být nastaveny cookies společnosti Google/YouTube. Více informací: <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Zásady ochrany soukromí Google</a></li>
      <li><strong>Google Fonts</strong> – webové fonty načítané ze serverů Google</li>
    </ul>
    <p>Technické cookies nezbytné pro fungování webu (např. uložení souhlasu s cookies) jsou ukládány na základě oprávněného zájmu.</p>

    <h2>7. Příjemci údajů</h2>
    <p>
      Vaše údaje nepředáváme třetím stranám s výjimkou poskytovatelů technických služeb (hosting, databáze), kteří údaje zpracovávají pouze na základě našich pokynů.
    </p>

    <h2>8. Vaše práva</h2>
    <p>Máte právo:</p>
    <ul>
      <li>Na <strong>přístup</strong> ke svým osobním údajům</li>
      <li>Na <strong>opravu</strong> nepřesných údajů</li>
      <li>Na <strong>výmaz</strong> údajů („právo být zapomenut")</li>
      <li>Na <strong>omezení zpracování</strong></li>
      <li>Na <strong>přenositelnost</strong> údajů</li>
      <li><strong>Vznést námitku</strong> proti zpracování</li>
      <li>Podat <strong>stížnost</strong> u Úřadu pro ochranu osobních údajů (<a href="https://www.uoou.cz" target="_blank" rel="noopener noreferrer">www.uoou.cz</a>)</li>
    </ul>

    <h2>9. Kontakt</h2>
    <p>
      V případě dotazů ohledně zpracování osobních údajů nás kontaktujte na: <a href="mailto:info@tomartmask.cz">info@tomartmask.cz</a>
    </p>
  </article>
);

const EnglishPrivacy = () => (
  <article className="prose prose-invert max-w-none prose-headings:text-foreground prose-p:text-muted-foreground prose-li:text-muted-foreground prose-strong:text-foreground">
    <h1>Privacy Policy</h1>
    <p className="text-sm text-muted-foreground">Last updated: {new Date().toLocaleDateString('en-US')}</p>

    <h2>1. Data Controller</h2>
    <p>
      The data controller is:<br />
      <strong>Tomáš Veselý</strong>, Sole Trader (OSVČ)<br />
      Business ID (IČO): <strong>[TO BE ADDED]</strong><br />
      Address: <strong>[TO BE ADDED]</strong><br />
      Email: <strong>info@tomartmask.cz</strong>
    </p>

    <h2>2. Personal Data We Process</h2>
    <p>Through the contact form and mask configurator on this website, we process:</p>
    <ul>
      <li>Full name</li>
      <li>Email address</li>
      <li>Phone number (optional)</li>
      <li>Message / inquiry content</li>
    </ul>
    <p>Through the chatbot, we may process:</p>
    <ul>
      <li>Email address (if provided)</li>
      <li>Phone number (if provided)</li>
      <li>Conversation content</li>
    </ul>

    <h2>3. Purpose of Processing</h2>
    <ul>
      <li><strong>Handling inquiries</strong> – responding to your message, preparing quotes, communication regarding orders</li>
      <li><strong>Legitimate interest</strong> – protecting the controller's rights, improving services</li>
    </ul>

    <h2>4. Legal Basis</h2>
    <p>
      Processing is based on <strong>Art. 6(1)(b) GDPR</strong> (performance of a contract / pre-contractual measures) and <strong>Art. 6(1)(f) GDPR</strong> (legitimate interest of the controller).
    </p>

    <h2>5. Data Retention</h2>
    <p>
      Personal data from contact forms is retained for the period necessary to handle the inquiry, up to a maximum of <strong>3 years</strong> from the last contact, unless longer retention is required by law.
    </p>

    <h2>6. Cookies and Third-Party Services</h2>
    <p>This website uses:</p>
    <ul>
      <li><strong>YouTube embeds</strong> – playing videos may set cookies by Google/YouTube. More info: <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google Privacy Policy</a></li>
      <li><strong>Google Fonts</strong> – web fonts loaded from Google servers</li>
    </ul>
    <p>Technical cookies necessary for website functionality (e.g., cookie consent storage) are stored based on legitimate interest.</p>

    <h2>7. Data Recipients</h2>
    <p>
      Your data is not shared with third parties except technical service providers (hosting, database) who process data solely based on our instructions.
    </p>

    <h2>8. Your Rights</h2>
    <p>You have the right to:</p>
    <ul>
      <li><strong>Access</strong> your personal data</li>
      <li><strong>Rectify</strong> inaccurate data</li>
      <li><strong>Erase</strong> your data ("right to be forgotten")</li>
      <li><strong>Restrict</strong> processing</li>
      <li><strong>Data portability</strong></li>
      <li><strong>Object</strong> to processing</li>
      <li>Lodge a <strong>complaint</strong> with the Czech Data Protection Authority (<a href="https://www.uoou.cz" target="_blank" rel="noopener noreferrer">www.uoou.cz</a>)</li>
    </ul>

    <h2>9. Contact</h2>
    <p>
      For questions regarding personal data processing, contact us at: <a href="mailto:info@tomartmask.cz">info@tomartmask.cz</a>
    </p>
  </article>
);

export default PrivacyPolicy;
